import { z } from "zod";

// This is a date class without any transform that includes all possible date formats we support in our systems.
export const TDate = z.union([
  z.object({ seconds: z.number(), nanoseconds: z.number() }), //firebase client sdk timestamp
  z.object({ _seconds: z.number(), _nanoseconds: z.number() }), //firebase admin sdk timestamp
  z.instanceof(Date),
  z.string().refine(
    (str) => {
      try {
        const date = new Date(str);
        // Make sure it's not an invalid date
        if (isNaN(date.getTime())) return false;
        // Verify it was really in ISO format by checking if it outputs the same
        // ISO string (with some flexibility for milliseconds precision)
        return date.toISOString().split(".")[0] === str.split(".")[0];
      } catch {
        return false;
      }
    },
    { message: "String must be a valid ISO date format" }
  ),
]);
export type IDate = z.infer<typeof TDate>;

export const TDateFormatToClientSDKFirebaseTimestamp = TDate.transform(
  (date) => {
    if (date instanceof Date)
      return { seconds: date.getTime() / 1000, nanoseconds: 0 };
    if (typeof date === "string")
      return { seconds: new Date(date).getTime() / 1000, nanoseconds: 0 };
    if (typeof date === "number") return { seconds: date, nanoseconds: 0 };
    if (typeof date === "object") {
      if ("seconds" in date && "nanoseconds" in date)
        return { seconds: date.seconds, nanoseconds: date.nanoseconds };
      if ("_seconds" in date && "_nanoseconds" in date)
        return { seconds: date._seconds, nanoseconds: date._nanoseconds };
    }
    throw new Error("Invalid date");
  }
);

export const TDateFormatToAdminSDKFirebaseTimestamp = TDate.transform(
  (date) => {
    if (date instanceof Date)
      return { _seconds: date.getTime() / 1000, _nanoseconds: 0 };
    if (typeof date === "string")
      return { _seconds: new Date(date).getTime() / 1000, _nanoseconds: 0 };
    if (typeof date === "number") return { _seconds: date, _nanoseconds: 0 };
    if (typeof date === "object") {
      if ("seconds" in date && "nanoseconds" in date)
        return { _seconds: date.seconds, _nanoseconds: date.nanoseconds };
      if ("_seconds" in date && "_nanoseconds" in date)
        return { _seconds: date._seconds, _nanoseconds: date._nanoseconds };
    }
    throw new Error("Invalid date");
  }
);

export const TDateFormatToJSDate = TDate.transform((date) => {
  if (date instanceof Date) return date;
  if (typeof date === "string") return new Date(date);
  if (typeof date === "number") return new Date(date);
  if (typeof date === "object") {
    if ("seconds" in date && "nanoseconds" in date)
      return new Date(date.seconds * 1000);
    if ("_seconds" in date && "_nanoseconds" in date)
      return new Date(date._seconds * 1000);
  }
  throw new Error("Invalid date");
});

export const TDateFormatToISOString = TDate.transform((date) => {
  if (date instanceof Date) return date.toISOString();
  if (typeof date === "string") return new Date(date).toISOString();
  if (typeof date === "number") return new Date(date).toISOString();
  if (typeof date === "object") {
    if ("seconds" in date && "nanoseconds" in date)
      return new Date(date.seconds * 1000).toISOString();
    if ("_seconds" in date && "_nanoseconds" in date)
      return new Date(date._seconds * 1000).toISOString();
  }
  throw new Error("Invalid date");
});
